import { useAppNotifications } from "@/hooks/useAppNotifications";
import FullScreenLoading from "@/components/FullScreenLoading";
import { APP_PATHS, ROUTE_PARAMS, TOAST_AUTO_HIDE_DURATION } from "@/constants";
import { accountsV2Query } from "@/context/account/accountsV2Query";
import { getDealerAccountRouteId } from "@/context/account/dealerAccountRouteId";
import { ME } from "@/context/me/MeProvider";
import type { SupportModeSession } from "@/context/supportMode";
import {
  DealerUserRole,
  type AccountsV2Query,
  type MeQuery,
} from "@/gql/graphql";
import { authSignOutRedirect } from "@/provider/AuthProvider/signOut";
import { applyAccountIdAuthPolicyForRole } from "@/provider/AuthProvider/accountIdAuthPolicy";
import { skipToken, useQuery } from "@/hooks/apollo";
import { useSupportMode } from "@/context/supportMode";

import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { Navigate, generatePath } from "react-router";
import { useEffect } from "react";

const resolveEntryRedirectPath = (
  accounts: AccountsV2Query["accountsV2"],
  meRole: MeQuery["me"]["role"] | undefined,
  supportModeSession: SupportModeSession | null
): string | null => {
  if (supportModeSession !== null) {
    return generatePath(`/:${ROUTE_PARAMS.ACCOUNT_ID}`, {
      accountId: getDealerAccountRouteId({
        id: supportModeSession.cdhId,
        iamId: supportModeSession.iamAccountId,
      }),
    });
  }

  const isSupportOrSalesAdmin =
    meRole === DealerUserRole.SupportAdmin ||
    meRole === DealerUserRole.SalesOrderAdmin;

  if (accounts.length === 0 && isSupportOrSalesAdmin) {
    return `/${APP_PATHS.SUPPORT_ADMIN}`;
  }

  if (accounts.length > 0) {
    const primaryAccount =
      accounts.find((account) => account.isPrimary) ?? accounts[0];

    if (primaryAccount !== undefined) {
      return generatePath(`/:${ROUTE_PARAMS.ACCOUNT_ID}`, {
        accountId: getDealerAccountRouteId({
          id: primaryAccount.cdhId,
          iamId: primaryAccount.id,
        }),
      });
    }
  }

  return null;
};

const getEntryDxpAccountId = (
  accounts: AccountsV2Query["accountsV2"],
  supportModeSession: SupportModeSession | null
): string | undefined => {
  if (supportModeSession !== null) {
    return supportModeSession.cdhId;
  }

  if (accounts.length === 0) {
    return undefined;
  }

  const primaryAccount =
    accounts.find((account) => account.isPrimary) ?? accounts[0];

  return primaryAccount?.cdhId;
};

export const AppEntryRedirect = () => {
  const { t } = useTranslation();
  const notification = useAppNotifications();
  const { signoutRedirect } = useAuth();
  const { session: supportModeSession } = useSupportMode();

  const accountsQuery = useQuery(accountsV2Query, {
    context: { skipGlobalError: true },
  });

  const accountsSettled =
    accountsQuery.dataState === "complete" ||
    (accountsQuery.error !== undefined && accountsQuery.dataState === "empty");

  const accounts: AccountsV2Query["accountsV2"] =
    accountsQuery.dataState === "complete" ? accountsQuery.data.accountsV2 : [];

  const shouldQueryMe = accountsQuery.dataState === "complete";
  const entryDxpAccountId = getEntryDxpAccountId(accounts, supportModeSession);

  const meQuery = useQuery(
    ME,
    shouldQueryMe
      ? {
          context: {
            skipGlobalError: true,
            ...(accounts.length === 0 ? { skipDxpIamHeader: true } : {}),
            ...(entryDxpAccountId !== undefined
              ? { dxpAccountIdOverride: entryDxpAccountId }
              : {}),
          },
        }
      : skipToken
  );

  const isLoading =
    !accountsSettled ||
    (shouldQueryMe && meQuery.loading && meQuery.data?.me === undefined);

  const meRole = meQuery.data?.me?.role;

  useEffect(() => {
    if (meRole != null) {
      applyAccountIdAuthPolicyForRole(meRole);
    }
  }, [meRole]);

  const isSupportOrSalesAdmin =
    meRole === DealerUserRole.SupportAdmin ||
    meRole === DealerUserRole.SalesOrderAdmin;

  const shouldSignOut =
    !isLoading && accounts.length === 0 && !isSupportOrSalesAdmin;

  if (isLoading) {
    return <FullScreenLoading title={t("accounts.loading")} />;
  }

  if (shouldSignOut) {
    notification.show(t("accounts.no_accounts"), {
      severity: "error",
      autoHideDuration: TOAST_AUTO_HIDE_DURATION,
    });
    setTimeout(() => {
      void authSignOutRedirect(signoutRedirect);
    }, 2000);
    return <FullScreenLoading title={t("accounts.logging_out")} />;
  }

  const redirectPath = resolveEntryRedirectPath(
    accounts,
    meRole,
    supportModeSession
  );

  if (redirectPath === null) {
    return null;
  }

  return <Navigate to={redirectPath} replace />;
};
